import React, { useMemo } from "react";
import useGetEmployeesOfTheMonth from "../../Hooks/useGET/useGetAllEmployeesOfTheMonth";
import LoadingSpinnerModal from "../../Components/LoadingSpinnerModal";
import ErrorMessage from "../../Components/ErrorMessage";
import "./styles.css";

// ─── Employee of the Year (Hardcoded. Not via the api yet) ───
// Keyed by year, value is the winner's name exactly as it comes back
// in user.name from the EOM api. Add a new line each year.
const EMPLOYEE_OF_THE_YEAR = {
  2025: "Scott May",
};

function formatMonth(iso) {
  try {
    const d = new Date(iso);
    return d.toLocaleDateString(undefined, { year: "numeric", month: "long" });
  } catch {
    return iso;
  }
}

function initials(name = "") {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((n) => n[0]?.toUpperCase() ?? "")
    .join("");
}

function EomCard({ item, featured = false }) {
  const { user, month, isCurrent } = item || {};
  const monthText = formatMonth(month);
  const pic = user?.profilePicture;
  const showImg = pic && !/blank\.jpg$/i.test(pic);
  const isEoy = isEmployeeOfTheYear(item);

  return (
    <div
      className={`eom-card ${featured ? "featured" : ""} ${
        isCurrent ? "current" : ""
      } ${isEoy ? "employee-of-year" : ""}`}
    >
      <div className="eom-card-media">
        {showImg ? (
          <img
            src={pic}
            alt={user?.name || "Employee"}
            onError={(e) => (e.currentTarget.style.display = "none")}
          />
        ) : (
          <div className="eom-avatar-fallback">{initials(user?.name)}</div>
        )}
      </div>
      <div className="eom-card-body">
        <div className="eom-month">{monthText}</div>
        <div className="eom-name">{user?.name || "—"}</div>
        {user?.assignedLocation && (
          <div className="eom-location">{user.assignedLocation}</div>
        )}
        {isEoy && <div className="eom-eoy-badge">Employee of the Year</div>}
      </div>
    </div>
  );
}

// Name-based match since the hardcoded list above only has names, not ids.
function isEmployeeOfTheYear(item) {
  const year = new Date(item?.month).getFullYear();
  const eoyName = EMPLOYEE_OF_THE_YEAR[year];
  return Boolean(eoyName) && item?.user?.name === eoyName;
}

export default function EmployeesOfTheMonthPage() {
  const { items, current, error, isLoading } = useGetEmployeesOfTheMonth(); // <-- pull from hook

  const listByYear = useMemo(() => {
    return items.reduce((acc, r) => {
      const y = new Date(r.month).getFullYear();
      (acc[y] ||= []).push(r);
      return acc;
    }, {});
  }, [items]);

  if (isLoading) return <LoadingSpinnerModal show />;
  if (error) return <ErrorMessage />;

  return (
    <div className="px-lg-4 content">
      <div className="px-2 page-content">
        <div className="contentBox my-3 eom-wrapper">
          <header className="eom-header">
            <h1 className="white">Employees of the Month</h1>
          </header>

          {current && (
            <section className="eom-featured">
              <EomCard item={current} featured />
            </section>
          )}

          <section className="eom-grid-section">
            {Object.keys(listByYear)
              .sort((a, b) => Number(b) - Number(a))
              .map((year) => (
                <div key={year} className="eom-year-block">
                  <h2 className="eom-year">{year}</h2>
                  <div className="eom-grid">
                    {listByYear[year]
                      .filter((item) => !item.isCurrent)
                      .map((item, i) => (
                        <EomCard
                          key={`${item.user?.id}-${item.month}-${i}`}
                          item={item}
                        />
                      ))}
                  </div>
                </div>
              ))}
          </section>
        </div>
      </div>
    </div>
  );
}
